import { getFeaturesForCompany, updateCompanyFeatureStatuses, FeatureDto } from "../interface/Features";
import {
  ALL_PAGE_KEYS,
  buildCodeToFeatureMap,
  isAlwaysEnabled,
  getPageEnabledState,
  getFeatureIdsForPage,
} from "../interface/Pagefeaturemapping";

export const PAGES_CHANGED_EVENT = "pagesPermissionsChanged";

let currentCompanyId: string | null = null;
let enabledPagesCache: string[] | null = null; 
let codeToFeatureCache: Map<string, FeatureDto> = new Map();
let inFlightFetch: Promise<void> | null = null;

const emitChanged = (companyId: string, pages: string[]) => {
  window.dispatchEvent(
    new CustomEvent(PAGES_CHANGED_EVENT, { detail: { companyId, pages } })
  );
};

export const loadEnabledPagesForCompany = async (
  companyId: string | undefined | null,
  forceRefresh = false
): Promise<string[]> => {
  if (!companyId || companyId === "undefined" || companyId === "null") {
    currentCompanyId = null;
    enabledPagesCache = null;
    return [];
  }

  if (!forceRefresh && currentCompanyId === companyId && enabledPagesCache !== null) {
    return enabledPagesCache;
  }

  if (!forceRefresh && inFlightFetch && currentCompanyId === companyId) {
    await inFlightFetch;
    return enabledPagesCache ?? [];
  }

  currentCompanyId = companyId;

  const fetchPromise = (async () => {
    const companyFeatures = await getFeaturesForCompany(companyId);
    const map = buildCodeToFeatureMap(companyFeatures);
    codeToFeatureCache = map;

    const enabledIds = new Set(
      companyFeatures.filter((f) => f.enable === true).map((f) => f.id)
    );

    const enabled = ALL_PAGE_KEYS.filter((key) =>
      getPageEnabledState(key, map, enabledIds)
    );

    enabledPagesCache = enabled;
    emitChanged(companyId, enabled);
  })();

  inFlightFetch = fetchPromise;
  try {
    await fetchPromise;
  } finally {
    if (inFlightFetch === fetchPromise) inFlightFetch = null;
  }

  return enabledPagesCache ?? [];
};


export const getEnabledPages = (
  companyId: string | undefined | null
): string[] | null => {
  if (!companyId || companyId === "undefined" || companyId === "null") return null;
  if (currentCompanyId !== companyId) return null;
  return enabledPagesCache;
};

export const isPageAllowed = (
  pageKey: string,
  companyId: string | undefined | null
): boolean => {
  if (pageKey === "dashboard") return true;
  const enabled = getEnabledPages(companyId);
  if (enabled === null) return true; 
  return enabled.includes(pageKey);
};

export const saveEnabledPages = async (
  companyId: string,
  pages: string[]
): Promise<void> => {
  if (!companyId || companyId === "undefined" || companyId === "null") {
    console.warn("saveEnabledPages: invalid companyId", companyId);
    return;
  }

  const statusMap = new Map<string, boolean>(); 

  ALL_PAGE_KEYS.forEach((key) => {
    if (isAlwaysEnabled(key)) return;
    const on = pages.includes(key);
    const featureIds = getFeatureIdsForPage(key, codeToFeatureCache);
    featureIds.forEach((id) => statusMap.set(id, on));
  });

  const statuses = Array.from(statusMap.entries()).map(([featureId, enabled]) => ({
    featureId,
    enabled,
  }));

  if (statuses.length > 0) {
    await updateCompanyFeatureStatuses(companyId, statuses);
  }

  currentCompanyId = companyId;
  enabledPagesCache = pages;
  emitChanged(companyId, pages);
};


export const getSelectedCompanyId = (): string | undefined => {
  try {
    const coStr = sessionStorage.getItem("selectedCompany");
    if (!coStr) return undefined;
    const companyObj = JSON.parse(coStr);
    const id = companyObj?.id;
    if (!id || id === "undefined" || id === "null") return undefined;
    return String(id);
  } catch {
    return undefined;
  }
};

export const getCurrentRole = (): string => {
  try {
    const uStr = sessionStorage.getItem("user");
    if (!uStr) return "";
    return JSON.parse(uStr)?.role ?? "";
  } catch {
    return "";
  }
};

const SYSTEM_ADMIN_ROLES = ["admin", "system admin", "system_admin"];
export const isSystemAdmin = (): boolean =>
  SYSTEM_ADMIN_ROLES.includes(getCurrentRole().toLowerCase().trim());